import { type MouseEvent, useRef, useState } from "react";
import "@/styles/v47-pages.css";

/** What's under the pointer: a column, and the segment in it when on one; where the tip sits. */
export type ColHover = { i: number; k?: string; left: number; top: number };

/**
 * The bar charts' hover (v43/v44 `chartTip`): the column is picked from where the pointer is, so it
 * lights whether the pointer is on a bar or in the gap beside it, and nothing dims. On a segment
 * the tip gives the segment; in the gap, the column's total. The tip appears on the first mousemove
 * in the app's type (not the browser's delayed `<title>`) and stays inside the chart.
 */
export function useColumnHover({
  n,
  L,
  step,
  W,
}: {
  n: number;
  L: number;
  step: number;
  W: number;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const tip = useRef<HTMLDivElement>(null);
  const [hover, setHover] = useState<ColHover | null>(null);

  const onMove = (ev: MouseEvent<SVGSVGElement>) => {
    const el = wrap.current;
    const svg = ev.currentTarget;
    if (!el) return;
    const seg = (ev.target as Element).closest?.("rect[data-k]");
    let i: number;
    if (seg) i = Number(seg.getAttribute("data-i"));
    else {
      const s = svg.getBoundingClientRect();
      if (!s.width) return setHover(null);
      const vx = ((ev.clientX - s.left) / s.width) * W;
      i = Math.floor((vx - L) / step);
      if (!(i >= 0 && i < n)) return setHover(null);
    }
    const r = el.getBoundingClientRect();
    const col = svg.querySelector(`rect.colhit[data-i="${i}"]`);
    const b = (seg ?? col)?.getBoundingClientRect();
    if (!b) return setHover(null);
    const th = tip.current?.offsetHeight ?? 0;
    setHover({
      i,
      k: seg?.getAttribute("data-k") ?? undefined,
      left: Math.min(Math.max(b.left + b.width / 2 - r.left, 58), r.width - 58),
      top: Math.max(th + 4, b.top - r.top + (seg ? -8 : 10)),
    });
  };
  return { wrap, tip, hover, onMove, onLeave: () => setHover(null) };
}

/** The transparent column rects (behind the bars), one per row, lit under the pointer. */
export function ColumnHits({
  n,
  L,
  step,
  T,
  bottom,
  on,
}: {
  n: number;
  L: number;
  step: number;
  T: number;
  bottom: number;
  on?: number;
}) {
  return (
    <>
      {Array.from({ length: n }, (_, i) => (
        <rect
          key={i}
          className={`colhit${on === i ? " on" : ""}`}
          data-i={i}
          x={(L + step * i).toFixed(1)}
          y={T}
          width={step.toFixed(1)}
          height={Math.max(0, bottom - T).toFixed(1)}
        />
      ))}
    </>
  );
}
